/**
 * Financial Empty State Component
 * Handles edge cases: no reservations, all paid, all pending
 * Phase 2: Confidence & Guidance
 */

const formatCurrency = (value) => {
  if (value === null || value === undefined || isNaN(value)) return '$0.00';
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(value);
};

export default function FinancialEmptyState({
  type = 'no_reservations',
  paidTotal = 0,
  pendingTotal = 0,
  ownerPayoutTotal = 0,
  nextPayoutDate = null,
  /** Month-only UX: e.g. "Jul 2025" */
  monthLabel = null,
  /** Label for jump button, e.g. "Oct 2025" or "Find a month with activity" */
  jumpTargetLabel = null,
  /** Called when user clicks jump; omit to hide button */
  onJumpToMonth = null,
}) {
  if (type === 'no_reservations') {
    const title = 'No booked reservations for this period.';
    const message = monthLabel
      ? `Try selecting a different date range.`
      : 'Try selecting a different date range to see your financials.';
    return (
      <div style={styles.container}>
        <div style={styles.icon}>📊</div>
        <div style={styles.title}>{title}</div>
        {message && <div style={styles.message}>{message}</div>}
        {jumpTargetLabel && onJumpToMonth && (
          <button
            type="button"
            onClick={onJumpToMonth}
            style={styles.jumpButton}
            aria-label={`Jump to ${jumpTargetLabel}`}
          >
            Jump to {jumpTargetLabel}
          </button>
        )}
      </div>
    );
  }

  if (type === 'all_paid') {
    return (
      <div style={styles.container}>
        <div style={styles.icon}>✓</div>
        <div style={styles.title}>All payouts complete</div>
        <div style={styles.message}>
          You earned {formatCurrency(ownerPayoutTotal)} this period, and all payouts have been deposited to your account.
        </div>
      </div>
    );
  }

  if (type === 'all_pending') {
    return (
      <div style={styles.container}>
        <div style={styles.icon}>⏳</div>
        <div style={styles.title}>Payouts pending</div>
        <div style={styles.message}>
          You earned {formatCurrency(ownerPayoutTotal)} this period. All payouts are pending and will be deposited soon.
          {nextPayoutDate && (
            <div style={styles.nextPayout}>
              Next payout expected: {new Date(nextPayoutDate).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
            </div>
          )}
        </div>
      </div>
    );
  }

  return null;
}

const styles = {
  container: {
    textAlign: 'center',
    padding: 'var(--spacing-8, 48px) var(--spacing-4, 16px)',
    backgroundColor: 'var(--color-bg-secondary, #f9fafb)',
    borderRadius: 'var(--radius-md, 6px)',
    border: '1px solid var(--color-border-light, #e5e7eb)',
  },
  icon: {
    fontSize: 'var(--font-size-4xl, 48px)',
    marginBottom: 'var(--spacing-4, 16px)',
  },
  title: {
    fontSize: 'var(--font-size-lg, 18px)',
    fontWeight: 'var(--font-weight-semibold, 600)',
    color: 'var(--color-text-primary, #111827)',
    marginBottom: 'var(--spacing-2, 8px)',
  },
  message: {
    fontSize: 'var(--font-size-base, 16px)',
    color: 'var(--color-text-secondary, #6b7280)',
    lineHeight: 1.6,
    maxWidth: '500px',
    margin: '0 auto',
  },
  nextPayout: {
    marginTop: 'var(--spacing-3, 12px)',
    fontSize: 'var(--font-size-sm, 14px)',
    color: 'var(--color-text-primary, #111827)',
    fontWeight: 'var(--font-weight-medium, 500)',
  },
  jumpButton: {
    marginTop: 'var(--spacing-4, 16px)',
    padding: 'var(--spacing-2, 8px) var(--spacing-4, 16px)',
    fontSize: 'var(--font-size-sm, 14px)',
    fontWeight: 'var(--font-weight-medium, 500)',
    color: '#ffffff',
    backgroundColor: 'var(--color-primary-base, #2563eb)',
    border: 'none',
    borderRadius: 'var(--radius-md, 6px)',
    cursor: 'pointer',
  },
};
